import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import usePersonScreening from "../hooks/usePersonScreening";
import ScreeningWarning from "./ScreeningWarning";
import usePawnerScreeningDetails from "../hooks/usePawnerScreeningDetails";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

/* ===================== Server-time helpers ===================== */
const OFFSET_KEY = "serverOffsetMs_v1";

// Get server time by reading the Date header (GMT) from same-origin
async function fetchServerNowUtc() {
  try {
    const res = await fetch(`${window.location.origin}/`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h); // GMT
  } catch {}
  try {
    const res = await fetch(`${window.location.origin}/favicon.ico`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}
  return new Date(); // fallback
}

// Initialize/reuse an offset so: serverNow ≈ new Date(Date.now() + offsetMs)
async function ensureServerOffset() {
  const cached = sessionStorage.getItem(OFFSET_KEY);
  if (cached != null && !Number.isNaN(Number(cached))) return Number(cached);
  const serverNow = await fetchServerNowUtc();
  const offset = serverNow.getTime() - Date.now();
  sessionStorage.setItem(OFFSET_KEY, String(offset));
  return offset;
}

// Always get “business now” using server offset
function businessNow() {
  const raw = sessionStorage.getItem(OFFSET_KEY);
  const offset = raw == null ? 0 : Number(raw);
  return new Date(Date.now() + (Number.isFinite(offset) ? offset : 0));
}

// Return a Date object that represents **Singapore’s calendar day** at 00:00
function businessTodaySG() {
  const stored = localStorage.getItem("customDate"); // optional override
  if (stored) {
    const m = String(stored).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const t = businessNow().getTime();
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [y, m, d] = fmt.format(new Date(t)).split("-").map(Number);
  return new Date(y, m - 1, d);
}
/* =============================================================== */

const Redeem = () => {
  useEscapeToGoBack();
  const navigate = useNavigate();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [redeemBy, setRedeemBy] = useState("Pawner");

  const [amountCollected, setAmountCollected] = useState("");
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  
  // Customer info for "Others"
  const [customerInfo, setCustomerInfo] = useState({
    nirc: "",
    name: "",
    nationality: "",
    sgAddr: "",
    contactNoMobile: "",
    dob: "",
  });

  const [staffCode, setStaffCode] = useState("");
  const [error, setError] = useState("");
  const [serverReady, setServerReady] = useState(false);

  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    // session info
    axios
      .get("https://www.lianfoong.com/api/staff/session", { withCredentials: true })
      .then((r) => r?.data?.staffCode && setStaffCode(r.data.staffCode))
      .catch(() => {});

    // server offset + autofocus
    let alive = true;
    (async () => {
      await ensureServerOffset();
      if (alive) setServerReady(true);
      requestAnimationFrame(() => {
        mmyyRef.current?.focus();
        mmyyRef.current?.select?.();
      });
    })();
    return () => {
      alive = false;
    };
  }, []);

  const extractYmd = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: +m[1], m: +m[2], d: +m[3] };
  };

  // Month computation pinned to SG calendar
  const computeMonthsPassedSG = (grantDateStr) => {
    const ymd = extractYmd(grantDateStr);
    if (!ymd) return 1;
    const g = new Date(ymd.y, ymd.m - 1, ymd.d);      // grant day (local Date)
    const t = businessTodaySG();                      // “today” in SG calendar
    let months = (t.getFullYear() - g.getFullYear()) * 12 + (t.getMonth() - g.getMonth());
    if (t.getDate() > g.getDate()) months += 1;
    return Math.max(1, months);
  };

  /* =========================================================================
     LOGIC: Interest & Total Calculation
  ========================================================================= */
  const financials = useMemo(() => {
    if (!transaction) return { interest: "0.00", total: "0.00" };

    const principal = Number(transaction.amount || 0);
    const months = computeMonthsPassedSG(transaction.grantDate);

    let rate = 0.015 * months;
    if (firstMonthDiscount) rate -= 0.005;
    if (rate < 0) rate = 0;

    const interestAmt = principal * rate;
    const totalAmt = principal + interestAmt;

    return {
      interest: interestAmt.toFixed(2),
      total: totalAmt.toFixed(2),
    };
  }, [transaction, firstMonthDiscount]);

  /* =========================================================================
     EFFECT 1: Set Default Discount on Load
  ========================================================================= */
  useEffect(() => {
    if (!transaction) {
      setFirstMonthDiscount(false);
      return;
    }
    const months = computeMonthsPassedSG(transaction.grantDate);
    // If 1 month, default to checked.
    setFirstMonthDiscount(months === 1);
  }, [transaction]);

  /* =========================================================================
     EFFECT 2: Sync Amount Collected
     Updates textbox when the calculated total changes (due to load or checkbox).
     Manual typing does not trigger this.
  ========================================================================= */
  useEffect(() => {
    setAmountCollected(financials.total);
  }, [financials]);


  const handleSearch = async () => {
    try {
      const { data } = await axios.get("https://www.lianfoong.com/api/pawn-transactions/search", {
        params: { mmyy, receiptNo },
      });
      setTransaction(data || null);
      setError(data ? "" : "Transaction not found!");
      if (!data) setAmountCollected("");
    } catch {
      setTransaction(null);
      setError("Transaction not found!");
      setAmountCollected("");
    }
  };

  const onlyMoney = (s = "") => String(s || "").replace(/[^\d.]/g, "");
  const toCents = (n) => Math.round(Number(n) * 100);

  // --- inactive guards ---
  const hasValue = (v) =>
    v !== null &&
    v !== undefined &&
    String(v).trim() !== "" &&
    String(v).toLowerCase() !== "null" &&
    String(v).toLowerCase() !== "undefined";

  const isTrueLike = (v) =>
    v === true || Number(v) === 1 || String(v).toLowerCase() === "true";

  const isFalseLike = (v) =>
    v === false || Number(v) === 0 || String(v).toLowerCase() === "false";

  const isInactive = useMemo(() => {
    if (!transaction) return false;
    return (
      isFalseLike(transaction.activeRecord) ||
      hasValue(transaction.redeemDate) ||
      (hasValue(transaction.redeemAmount) && Number(transaction.redeemAmount) > 0) ||
      hasValue(transaction.redeemBy) ||
      isTrueLike(transaction.transactionStatus)
    );
  }, [transaction]);

  const pawnerScreeningDetails = usePawnerScreeningDetails(transaction?.receiptNo, redeemBy === "Pawner" && !isInactive);
  const screening = usePersonScreening(
    redeemBy === "Others" ? customerInfo.name : pawnerScreeningDetails.customer?.name,
    redeemBy === "Others" ? customerInfo.dob : pawnerScreeningDetails.customer?.dob,
    { enabled: !!transaction && !isInactive, context: JSON.stringify([transaction, redeemBy, amountCollected, firstMonthDiscount, customerInfo]) }
  );

  const handleSubmit = async () => {
    if (!transaction) {
      alert("No transaction found!");
      return;
    }
    if (isInactive) {
      alert("Ticket is deactivated and cannot be redeemed.");
      return;
    }

    if (redeemBy === "Pawner" && !pawnerScreeningDetails.customer?.name && !pawnerScreeningDetails.error) {
      alert(pawnerScreeningDetails.error || "Pawner details are loading. Please wait before redeeming.");
      return;
    }
    if (!(await screening.confirmBeforeProceed())) return;

    // Validate total against expected (from financials memo)
    const principal = Number(transaction.amount || 0);
    const expectedTotal = Number(financials.total);
    const expectedInterest = Number(financials.interest);

    const raw = onlyMoney(amountCollected);
    let enteredTotal = Number(raw);

    const issues = [];
    const invalid = !raw || !Number.isFinite(enteredTotal) || enteredTotal < 0;

    if (invalid) {
      issues.push("No/invalid amount entered. It will be treated as $0.00.");
      enteredTotal = 0;
    } else if (Math.abs(toCents(enteredTotal) - toCents(expectedTotal)) >= 1) {
      issues.push(
        `Entered total ($${enteredTotal.toFixed(2)}) ≠ expected ($${expectedTotal.toFixed(2)}).`
      );
    }

    if (issues.length) {
      const proceed = window.confirm(
        `Please confirm:\n\n${issues.map((s) => `• ${s}`).join("\n")}\n\n` +
          `Breakdown:\n` +
          `- Principal: $${principal.toFixed(2)}\n` +
          `- Interest:  $${expectedInterest.toFixed(2)}${
            firstMonthDiscount ? " (−0.5% discount applied)" : ""
          }\n` +
          `- Expected Total: $${expectedTotal.toFixed(2)}\n\n` +
          `Continue anyway?`
      );
      if (!proceed) return;
      if (invalid) setAmountCollected("0.00");
    }

    const enteredHandler = prompt("Please enter your handler code:");
    if (!enteredHandler) {
      alert("Handler code is required!");
      return;
    }

    try {
      const payload = {
        redeemAmount: amountCollected || "0.00",
        redeemBy: redeemBy === "Pawner" ? transaction.pawnerNirc : customerInfo.nirc,
        redeemHandler: enteredHandler,
        transactionStatus: 1,
      };

      await axios.put(
        `https://www.lianfoong.com/api/pawn-transactions/redeem?receiptNo=${transaction.receiptNo}`,
        payload,
        { withCredentials: true }
      );

      if (redeemBy === "Others") {
        await axios.post("https://www.lianfoong.com/api/customers", { ...customerInfo, dob: customerInfo.dob || null }, {
          withCredentials: true,
        });
      }

      alert("Redemption successful!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Redeem failed:", error);
      alert("Failed to process redemption.");
    }
  };

  const formatDateDDMMYYYY = (dateString) => {
    const ymd = extractYmd(dateString);
    if (!ymd) return "";
    const day = String(ymd.d).padStart(2, "0");
    const month = String(ymd.m).padStart(2, "0");
    const year = ymd.y;
    return `${day}/${month}/${year}`;
  };

  // Enter key behavior
  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
      receiptRef.current?.select?.();
    }
  };
  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (transaction && !isInactive) handleSubmit();
      else handleSearch();
    }
  };

  return (
    <div className="renew-container">
      <h2 className="title">Redeem Pawn Ticket</h2>

      {!serverReady && (
        <p style={{ color: "#555", marginBottom: 8 }}>
          Loading server time… You can still search; calculations will sync shortly.
        </p>
      )}

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          type="text"
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(e.target.value)}
          onKeyDown={onMmyyKeyDown}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          type="text"
          ref={receiptRef}
          value={receiptNo}
          onChange={(e) => setReceiptNo(e.target.value)}
          onKeyDown={onReceiptKeyDown}
        />
      </div>

      <button className="search-btn" onClick={handleSearch}>
        Search
      </button>

      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount).toFixed(2)}</p>
            <p><strong>Months Pass:</strong> {computeMonthsPassedSG(transaction.grantDate)}</p>
            <p><strong>Grant Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="discount"
              checked={firstMonthDiscount}
              onChange={(e) => setFirstMonthDiscount(e.target.checked)}
              disabled={isInactive}
            />
            <label htmlFor="discount">First Month Interest Discount (-0.5%)</label>
          </div>

          <div className="details-grid">
            <p><strong>Interest (auto):</strong> ${financials.interest}</p>
            <p><strong>Amount + Interest (auto):</strong> {financials.total}</p>
          </div>

          <div className="form-group">
            <label>Amount Collected (Total + Interest):</label>
            <input
              type="text"
              value={amountCollected}
              onChange={(e) => setAmountCollected(onlyMoney(e.target.value))}
              disabled={isInactive}
            />
          </div>

          {!isInactive && <ScreeningWarning screening={screening} error={redeemBy === "Pawner" ? pawnerScreeningDetails.error : ""} />}

          <div className="form-group">
            <label>Redeem By:</label>
            <select value={redeemBy} onChange={(e) => setRedeemBy(e.target.value)} disabled={isInactive}>
              <option value="Pawner">Pawner</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {redeemBy === "Others" && !isInactive && (
            <div className="customer-details">
              <div className="form-group">
                <label>NRIC:</label>
                <input
                  type="text"
                  value={customerInfo.nirc}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, nirc: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Birthdate (optional, to distinguish people with the same name):</label>
                <input type="date" value={customerInfo.dob} onChange={(e) => setCustomerInfo({ ...customerInfo, dob: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nationality:</label>
                <input
                  type="text"
                  value={customerInfo.nationality}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, nationality: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Address:</label>
                <textarea
                  rows="3"
                  value={customerInfo.sgAddr}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, sgAddr: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Mobile No:</label>
                <input
                  type="text"
                  value={customerInfo.contactNoMobile}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, contactNoMobile: e.target.value })}
                />
              </div>
            </div>
          )}

          {isInactive && (
            <p className="error-message" style={{ marginTop: 12 }}>
              Ticket is inactive.
            </p>
          )}

          <button
            className="next-btn"
            onClick={handleSubmit}
            disabled={isInactive}
            title={isInactive ? "Ticket is inactive and cannot be redeemed." : "Submit redemption"}
            aria-disabled={isInactive ? "true" : "false"}
          >
            Submit
          </button>
        </div>
      )}
    </div>
  );
};

export default Redeem;